import type { FrontendInstallContext } from "@p0security/integrations-installer";
import { lookerAiAccessTokenPrefix } from "@p0security/integrations-looker-ai-shared/secret-manager";
import type { LookerAiComponents } from "@p0security/integrations-looker-ai-shared/types";
import {
  gcpConnectorTf,
  p0InvokerEmail,
  secretProviderFor,
  shellLines,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { ItemConfigOf } from "@p0security/shared/install/types";

type LookerAiItem = ItemConfigOf<LookerAiComponents["credential"]>;

export type GcpSecretManagerConfig = Extract<
  LookerAiItem["secretManager"],
  { type: "gcp-sm" }
>;

export type InstallContext = FrontendInstallContext<unknown>;

const GCP_CONNECTOR_IMAGE = {
  imageName: "docker.io/p0security/p0-connector-looker-ai-gcloud",
  digest:
    "sha-587fbe5@sha256:e986310f36a13b95b1e6c926359d7af78a58e98c9c3841d03a268a607e1aceaf",
} as const;

/** The Terraform resource that holds the Admin key. The install applies it
 * first, so the key can be added before the connector starts. */
const renderAdminKeySecretAddress = (itemId: string) =>
  `google_secret_manager_secret.p0_looker_admin_key_${tfId(itemId)}`;

/** Applies only the Admin key's secret. */
export const renderCreateAdminKeySecret = (itemId: string) =>
  `terraform apply -target=${renderAdminKeySecretAddress(itemId)}`;

/** Adds the Admin key as a new version of its secret, as the JSON that the
 * connector reads. The key never goes through Terraform, so it never lands in
 * the Terraform state.
 *
 * The secret's name and the project are safe to paste into a shell: the
 * install form takes only letters, digits, hyphens and underscores for the
 * name, and the project is an installed Google Secret Manager project. */
export const renderStoreAdminKey = (
  adminKeySecret: string,
  secretManager: GcpSecretManagerConfig
) =>
  shellLines(
    `printf '{"client_id":"%s","client_secret":"%s"}' "$LOOKER_CLIENT_ID" "$LOOKER_CLIENT_SECRET"`,
    `| gcloud secrets versions add ${adminKeySecret}`,
    `--project=${secretManager.install}`,
    "--data-file=-"
  );

export const GCP_APPLY_ALL = "terraform apply";

/** Terraform that deploys the instance's connector on Cloud Run, lets it
 * manage only the instance's grant secrets, and lets it read the Admin key. */
export const renderGcpTf = (
  context: InstallContext,
  itemId: string,
  item: Pick<LookerAiItem, "adminKeySecret">,
  secretManager: GcpSecretManagerConfig
) =>
  gcpConnectorTf({
    names: { resource: "looker", role: "Looker", label: "Looker" },
    image: GCP_CONNECTOR_IMAGE,
    projectId: secretManager.install,
    region: secretManager.connectorRegion,
    connectorName: secretManager.connectorServiceName,
    serviceAccountId:
      secretManager.connectorServiceAccount?.split("@")[0] ?? "",
    invokerEmail: p0InvokerEmail(context.installed),
    tokenSecretPrefix: secretProviderFor("gcp").replacer(
      lookerAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "admin_key",
      itemTfId: tfId(itemId),
      // The customer names the Admin key's secret at install.
      secretId: item.adminKeySecret,
      label: "Admin key",
    },
  });
